import { DOCUMENT, inject } from "@angular/core";
import { NavigationError } from "@angular/router";

const CHUNK_LOAD_ERROR_MESSAGES = [
  "Failed to fetch dynamically imported module", // Chrome
  "error loading dynamically imported module", // Firefox
  "Importing a module script failed", // Safari
];

const LAST_RELOAD_KEY = "chunkLoadErrorReloadAt";
const MIN_RELOAD_GAP_MS = 10 * 1000;

// Keyed by the window being reloaded; the scheduled load replaces it.
const reloadingWindows = new WeakSet<object>();

export function isChunkLoadErrorMessage(message: unknown): boolean {
  return (
    typeof message === "string" &&
    CHUNK_LOAD_ERROR_MESSAGES.some((candidate) => message.includes(candidate))
  );
}

// The router rejects the failed navigation after this handler returns, so the
// rejection still reaches Sentry while the page is unloading.
export function isChunkLoadReloadScheduled(view: object = window): boolean {
  return reloadingWindows.has(view);
}

/**
 * A tab opened before a deploy asks for lazy chunks the server no longer has,
 * so the navigation fails without any visible effect. Loading the target url
 * from the server picks up the current build. A second failure within the gap
 * is left alone so a genuinely missing chunk cannot cause a reload loop.
 */
export function reloadOnChunkLoadError(error: NavigationError): void {
  if (!isChunkLoadErrorMessage(error.error?.message)) {
    return;
  }

  const window = inject(DOCUMENT).defaultView;
  const now = Date.now();
  const lastReloadAt = Number(window.sessionStorage.getItem(LAST_RELOAD_KEY));

  if (now - lastReloadAt < MIN_RELOAD_GAP_MS) {
    return;
  }

  window.sessionStorage.setItem(LAST_RELOAD_KEY, String(now));
  reloadingWindows.add(window);
  window.location.assign(error.url);
}
